import * as React from "react";
import styled from "styled-components";
import color from "../../../utils/color";
import RightArrowIcon from "src/icons/RightArrowIcon";
import LeftArrowIcon from "src/icons/LeftArrowIcon";

const NumberBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const NumberBox = styled.div<{ isSelected?: boolean; isNumber?: boolean }>`
  height: 35px;
  text-align: center;
  line-height: 35px;
  color: ${({ isSelected }) =>
    isSelected ? color.PRIMARY : color.ICONS_HEADER};
  font-size: 14px;
  width: 35px;
  font-weight: 600;
  background-color: ${({ isNumber }) =>
    isNumber ? color.BACKGROUND_SECONDARY : color.BACKGROUND};
  border-radius: 3px;
  margin-right: 5px;
  cursor: ${({ isNumber, isSelected }) =>
    isSelected ? "default" : isNumber ? "pointer" : "default"};

  &:last-child {
    margin-right: 0;
  }
`;

const IconsWrapper = styled.div`
  height: 35px;
  width: 35px;
  transform: translateY(3px);
  color: ${color.NEUTRAL};
  font-size: 30px;
  cursor: pointer;
`;

type Props = {
  dataLength: number;
  perPage: number;
  activePage: number;
  onClick(value: number): () => void;
};

type State = { lastPage: number };

class Pagination extends React.Component<Props, State> {
  public state = {
    lastPage: Math.ceil(this.props.dataLength / this.props.perPage),
  };

  public componentDidUpdate(prevProps: Props) {
    if (
      prevProps.dataLength !== this.props.dataLength ||
      prevProps.perPage !== this.props.perPage
    ) {
      this.setState({
        lastPage: Math.ceil(this.props.dataLength / this.props.perPage),
      });
    }
  }

  public render() {
    const { dataLength, perPage } = this.props;

    return (
      dataLength > perPage && (
        <NumberBoxWrapper>
          {this.arrows(true)}
          {this.renderNavigationPages()}
          {this.arrows(false)}
        </NumberBoxWrapper>
      )
    );
  }

  private arrows = (leftSide: boolean) => {
    const { activePage, onClick } = this.props;
    const { lastPage } = this.state;
    if (activePage > 1 && leftSide) {
      return (
        <IconsWrapper>
          <LeftArrowIcon onClick={onClick(activePage - 1)} />
        </IconsWrapper>
      );
    }
    if (activePage < lastPage && !leftSide) {
      return (
        <IconsWrapper>
          <RightArrowIcon onClick={onClick(activePage + 1)} />
        </IconsWrapper>
      );
    }
    return <NumberBox />;
  };

  private renderNavigationPages = () => {
    const { lastPage } = this.state;
    const { activePage, onClick } = this.props;
    const pagesToShow: Array<number | string> = [];
    pagesToShow.push(1);
    if (lastPage > 5) {
      let start: number, end: number;
      if (activePage <= 5) {
        start = 2;
        end = Math.min(5, lastPage - 1);
      } else if (activePage >= lastPage - 4) {
        start = lastPage - 4;
        end = lastPage - 1;
      } else {
        start = activePage - 1;
        end = activePage + 1;
      }
      for (let i = start; i <= end; i++) {
        pagesToShow.push(i);
      }
      if (start > 2) pagesToShow.splice(1, 0, "...");
      if (end < lastPage - 1) pagesToShow.push("...");
    } else {
      for (let i = 2; i < lastPage; i++) pagesToShow.push(i);
    }
    if (lastPage > 1) pagesToShow.push(lastPage);
    return pagesToShow.map((page, index) => {
      if (page === "...") {
        const isLeftDots =
          pagesToShow[index - 1] !== "..." && index < pagesToShow.length / 2;
        return (
          <NumberBox key={isLeftDots ? "dots-left" : "dots-right"}>
            ...
          </NumberBox>
        );
      }
      return (
        <NumberBox
          key={`nav-page-${page}`}
          onClick={onClick(Number(page))}
          isSelected={page === activePage}
          isNumber
        >
          {page}
        </NumberBox>
      );
    });
  };
}

export default Pagination;
